/**
 * Client-side module-entitlement helpers (ADR-0023 §4, ADR-0046).
 *
 * The module twin of `usePermission` — reads the already-cached `identity.me`
 * response (no extra round-trip) and answers "is this workspace entitled to
 * module X?". Use it to hide affordances that lead to a withheld module so they
 * don't dead-end at the route guard (`requireModule`) which redirects home.
 *
 * `isModuleEntitled` from `@flint/api-contract` is the single source of the
 * gate's decision — the same predicate `entitledProcedureFor` uses server-side.
 * These hooks are UX softening only; the server + route guard are the floor.
 *
 * @see packages/api-contract/src/modules.ts — catalog + isModuleEntitled
 * @see apps/web/src/features/workspace/lib/module-guard.ts — the route guard
 */
import { useQuery } from "@tanstack/react-query";
import { isModuleEntitled } from "@flint/api-contract";
import type { AppModule } from "@flint/api-contract";
import { meQueryOptions } from "./session.ts";

/** The active workspace's entitled module strings, or `[]` when none/unauthenticated. */
export function useModules(): readonly string[] {
  const { data } = useQuery(meQueryOptions);
  return data?.company?.modules ?? [];
}

/** `true` when the active workspace is entitled to `module`. */
export function useModule(module: AppModule): boolean {
  return isModuleEntitled(useModules(), module);
}
